import { formatMoney, getCurrency, getLocale } from '../../../helpers/utils'
import { formatReportRatio } from './reportRatio'

export const KPI_VALUE_TYPES = {
  COUNT: 'count',
  CURRENCY: 'currency',
  PERCENT: 'percent',
  RATIO: 'ratio',
  TEXT: 'text'
}

const COMPACT_UNITS = [
  { threshold: 1e12, suffix: 'T' },
  { threshold: 1e9, suffix: 'B' },
  { threshold: 1e6, suffix: 'M' },
  { threshold: 1e3, suffix: 'K' }
]

function normalizeNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function roundHalfEven(value, decimals = 1) {
  const factor = 10 ** decimals
  const scaled = value * factor
  const floor = Math.floor(scaled)
  const diff = scaled - floor
  const epsilon = 1e-10

  if (diff > 0.5 + epsilon) return (floor + 1) / factor
  if (diff < 0.5 - epsilon) return floor / factor
  return (floor % 2 === 0 ? floor : floor + 1) / factor
}

function formatPlainNumber(value) {
  return new Intl.NumberFormat(getLocale(), {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
    useGrouping: false
  }).format(value)
}

function formatCompactNumber(value) {
  const normalized = normalizeNumber(value)
  const absolute = Math.abs(normalized)

  if (absolute < 1000) {
    return formatPlainNumber(normalized)
  }

  const unitIndex = COMPACT_UNITS.findIndex((unit) => absolute >= unit.threshold)
  const unit = COMPACT_UNITS[unitIndex]
  let compactValue = roundHalfEven(absolute / unit.threshold, 1)
  let nextUnit = unit

  if (compactValue >= 1000 && unitIndex > 0) {
    nextUnit = COMPACT_UNITS[unitIndex - 1]
    compactValue = roundHalfEven(absolute / nextUnit.threshold, 1)
  }

  const sign = normalized < 0 ? '-' : ''
  return `${sign}${formatPlainNumber(compactValue)}${nextUnit.suffix}`
}

function getCurrencyParts() {
  const parts = new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: getCurrency(),
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).formatToParts(1)

  const currencyPart = parts.find((part) => part.type === 'currency')
  const integerIndex = parts.findIndex((part) => part.type === 'integer')
  const currencyIndex = parts.findIndex((part) => part.type === 'currency')
  const literalBetween = parts
    .slice(Math.min(integerIndex, currencyIndex) + 1, Math.max(integerIndex, currencyIndex))
    .filter((part) => part.type === 'literal')
    .map((part) => part.value)
    .join('')

  return {
    currencySymbol: currencyPart?.value || getCurrency(),
    isPrefix: currencyIndex >= 0 && currencyIndex < integerIndex,
    literalBetween
  }
}

function formatCompactCurrency(value) {
  const normalized = normalizeNumber(value)
  const sign = normalized < 0 ? '-' : ''
  const { currencySymbol, isPrefix, literalBetween } = getCurrencyParts()
  const compactValue = formatCompactNumber(Math.abs(normalized))

  if (isPrefix) {
    return `${sign}${currencySymbol}${literalBetween}${compactValue}`
  }

  return `${sign}${compactValue}${literalBetween}${currencySymbol}`
}

function formatRatio(valueConfig, compact) {
  return formatReportRatio(
    valueConfig.numerator,
    valueConfig.denominator,
    compact ? formatCompactNumber : (value) => formatPlainNumber(normalizeNumber(value))
  )
}

export function formatKpiValue(valueConfig, { compact = false } = {}) {
  if (!valueConfig || typeof valueConfig !== 'object') {
    return String(valueConfig ?? '')
  }

  switch (valueConfig.type) {
    case KPI_VALUE_TYPES.COUNT:
      return compact
        ? formatCompactNumber(valueConfig.value)
        : formatPlainNumber(normalizeNumber(valueConfig.value))
    case KPI_VALUE_TYPES.CURRENCY:
      return compact ? formatCompactCurrency(valueConfig.value) : formatMoney(valueConfig.value)
    case KPI_VALUE_TYPES.PERCENT:
      return `${formatPlainNumber(normalizeNumber(valueConfig.value))}%`
    case KPI_VALUE_TYPES.RATIO:
      return formatRatio(valueConfig, compact)
    case KPI_VALUE_TYPES.TEXT:
      return String(valueConfig.value ?? '')
    default:
      return String(valueConfig.value ?? '')
  }
}

export function formatKpiValueTitle(valueConfig) {
  return formatKpiValue(valueConfig, { compact: false })
}

export function isRatioValue(valueConfig) {
  return valueConfig?.type === KPI_VALUE_TYPES.RATIO
}
